import { FC, ReactNode } from 'react';
import type { SopCobertura, SopFrescor, SopMes } from '@/types/dashboards';
import { descreverCobertura } from '@/utils/equiplexSop';
import { fmtData, fmtDataHora, mesCurto } from '@/utils/painelFormat';
import Aviso from '../Aviso';
import { STATUS } from '../chartTheme';

interface Props {
  frescor: SopFrescor;
  cobertura: SopCobertura[];
  mesEmAndamento: SopMes | undefined;
  meses: Record<string, SopMes>;
  /** Último dia fechado (corte − 2 dias). */
  fechadoAte: string;
}

/** Tudo o que muda a leitura dos números: réplica parada, faturamento fora do normal, mês parcial. */
const SopAvisos: FC<Props> = ({ frescor, cobertura, mesEmAndamento, meses, fechadoAte }) => {
  const avisos: ReactNode[] = [];
  if (frescor.replica_atrasada) {
    avisos.push(
      <Aviso key="frescor" testId="aviso-replica" icone="cloud_off" cor={STATUS.critico} titulo="Réplica do Datasul parada">
        A última carga foi em {fmtDataHora(frescor.ultima_carga_replica)}
        {frescor.dias_sem_carga !== null && <> ({frescor.dias_sem_carga} dias)</>}. Produção e faturamento vão só até{' '}
        {fmtData(frescor.producao_ate)} — o que aconteceu depois ainda não chegou no banco.
      </Aviso>,
    );
  }
  if (cobertura.length > 0) {
    avisos.push(
      <Aviso key="cobertura" testId="aviso-cobertura" icone="warning" cor={STATUS.atencao} titulo="Faturamento bem abaixo do normal">
        {cobertura.map((c, i) => (
          <span key={c.mes}>
            {i > 0 && '; '}
            <strong>{mesCurto(meses[c.mes]?.label ?? c.mes)}</strong>: {descreverCobertura(c)}
          </span>
        ))}
        . O esperado é a mediana dos seis meses anteriores, proporcional aos dias corridos. Desde 22/09/2026 a réplica só
        troca uma tabela quando a carga nova chega inteira, então número baixo aqui é venda baixa de fato — vale confirmar
        com o comercial.
      </Aviso>,
    );
  }
  if (mesEmAndamento) {
    avisos.push(
      <Aviso key="parcial" testId="aviso-parcial" icone="hourglass_top" cor={STATUS.atencao} titulo={`${mesEmAndamento.label} ainda está aberto`}>
        O desvio só olha as OPs com início até {fmtData(fechadoAte)} (dias fechados): a OP é reportada de uma vez no fim do
        processo, então as dos dias mais recentes ainda estão chegando e ficam de fora até o dia fechar. Programado e
        produzido saem das mesmas OPs; os totais do mês até agora estão no tooltip do gráfico de volume e crescem até as
        OPs fecharem.
      </Aviso>,
    );
  }
  if (avisos.length === 0) return null;
  return <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">{avisos}</div>;
};

export default SopAvisos;
